import React, { useEffect, useRef, useState } from "react";

import { useHistory } from "react-router-dom";
import { ActionButton, Button, classnames, Icon, Loader, ModalDialog } from "@culturehq/components";
import { makeGet, makePatch } from "@culturehq/client";

import { useProfile } from "app/context";
import { useDoor } from "app/hooks";
import { getImageFromStory, t, Trans } from "app/utils";
import { MAX_TIKTOK_VIDEO_DURATION } from "app/utils/constants";
import general from "constants/general";
import imagesSizes from "constants/imagesSizes";

import { Time } from "components/common/Time";
import { tiktokAuthenticated } from "components/profile/ProfileSocial";

import { getDefaultStartsAt } from "constants/eventTypes";

import ChqModalDialog from "components/common/ChqModalDialog";
import EmptyPreview from "../previews/EmptyPreview";
import FacebookPreview from "../previews/FacebookPreview";
import InstagramPreview from "../previews/InstagramPreview";
import LinkedinPreview from "../previews/LinkedinPreview";
import TiktokPreview from "../previews/TiktokPreview";
import FormData from "./FormData";

const PreviewCard = ({ category, data, profile, socialPostType, story }) => {
  const mediaRef = React.createRef();
  const [showVideoInfo, setShowVideoInfo] = useState(false);
  const [imageUrl] = useState(story.media.mediaType === "image" ? getImageFromStory(imagesSizes.NORMAL, story) : story.media.thumbnail);

  const handlePlay = () => {
    mediaRef.current.play();
  };

  const handleShowVideoInfo = value => {
    setShowVideoInfo(value);
  };

  switch (category) {
    case "Facebook":
      return (
        <div className="SocialMedia-Preview" key={category}>
          <FacebookPreview
            mediaRef={mediaRef}
            showVideoInfo={showVideoInfo}
            onShowVideoInfo={handleShowVideoInfo}
            onPlay={handlePlay}
            imageUrl={imageUrl}
            data={data}
            profile={profile}
            story={story}
          />
        </div>
      );
    case "Instagram":
      return (
        <div className="SocialMedia-Preview SocialMedia-Preview-Instagram" key={category}>
          <InstagramPreview
            mediaRef={mediaRef}
            showVideoInfo={showVideoInfo}
            onShowVideoInfo={handleShowVideoInfo}
            onPlay={handlePlay}
            imageUrl={imageUrl}
            data={data}
            profile={profile}
            story={story}
          />
        </div>
      );
    case "LinkedIn":
      return (
        <div className="SocialMedia-Preview" key={category}>
          <LinkedinPreview
            mediaRef={mediaRef}
            showVideoInfo={showVideoInfo}
            onShowVideoInfo={handleShowVideoInfo}
            onPlay={handlePlay}
            imageUrl={imageUrl}
            data={data}
            profile={profile}
            socialPostType={socialPostType}
            story={story}
          />
        </div>
      );
    case "TikTok":
      return (
        <div className="SocialMedia-Preview SocialMedia-Preview-Tiktok" key={category}>
          <TiktokPreview
            mediaRef={mediaRef}
            showVideoInfo={showVideoInfo}
            onShowVideoInfo={handleShowVideoInfo}
            onPlay={handlePlay}
            imageUrl={imageUrl}
            data={data}
            profile={profile}
            socialPostType={socialPostType}
            story={story}
          />
        </div>
      );
    case "Twitter":
      break;
    default:
      break;
  }

  return <></>;
};

const ApproveContentModal = ({
  approvedContent,
  children,
  externalState,
  onCloseModal,
  onShareToQRLink,
  onOpenChildren,
  onUpdateApprovedForSocial,
  story
}) => {
  const isMounted = useRef(true);
  const { profile } = useProfile();
  const { organization } = profile;
  const history = useHistory();
  const { open, onOpen, onClose } = useDoor(false);
  const [initialShareMessage, setInitialShareMessage] = useState();
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const [pendingLinkedAccounts, setPendingLinkedAccounts] = useState([]);

  const [linkedinOrganizations, setLinkedinOrganizations] = useState([]);
  const [linkedinOrganizationsIndex, setLinkedinOrganizationsIndex] = useState({});

  const [facebookPages, setFacebookPages] = useState([]);
  const [tiktokAccounts, setTiktokAccounts] = useState([]);

  const [instagramAccounts, setInstagramAccounts] = useState([]);
  const [instagramAccountsIndex, setInstagramAccountsIndex] = useState({});

  const [socialPostType, setSocialPostType] = useState("business");
  const [schedulePostType, setSchedulePostType] = useState("no");
  const [companyAccounts, setCompanyAccounts] = useState([]);
  const [personalAccounts, setPersonalAccounts] = useState([]);
  const [selectedAccounts, setSelectedAccounts] = useState([]);

  const [socialMediaSelected, setSocialMediaSelected] = useState([]);
  const [uniqueSocialMedia, setUniqueSocialMedia] = useState([]);

  const [timezoneOffset, setTimezoneOffset] = useState(-new Date().getTimezoneOffset());
  const [scheduleAt, setScheduleAt] = useState();
  const [error, setError] = useState();
  const isTiktokShareable = story.media?.duration >= 3
    && story.media?.duration <= MAX_TIKTOK_VIDEO_DURATION;

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, [open]);

  useEffect(
    () => {
      if (schedulePostType === "yes") {
        setScheduleAt(getDefaultStartsAt());
      } else {
        setScheduleAt();
      }
    },
    [schedulePostType]
  );

  useEffect(
    () => {
      setUniqueSocialMedia(socialMediaSelected.filter((value, index, self) => (
        self.map(media => media.category).indexOf(value.category) === index
      )));
    }, [socialMediaSelected]
  );

  useEffect(
    () => {
      const fetchLinkedinOrganizations = async () => {
        const { organizations } = await makeGet("/linkedin/organizations");
        if (!isMounted.current) return;

        const orgsIndex = {};
        const results = [];
        organizations.forEach(organization => {
          results.push({
            label: organization.localizedName,
            value: organization.$URN,
            category: "LinkedIn"
          });
          orgsIndex[organization.$URN] = organization;
        });
        setLinkedinOrganizationsIndex(orgsIndex);
        setLinkedinOrganizations(results);
      };

      const fetchFacebookPages = async () => {
        const { instagram, pages } = await makeGet("/facebook/pages");
        if (!isMounted.current) return;

        const results = [];
        pages.forEach(page => {
          results.push({ label: page.name, value: page.externalId, category: "Facebook" });
        });
        setFacebookPages(results);

        const instagramAccountsInfo = {};
        const instagramResults = [];
        instagram.forEach(instagramAccount => {
          instagramResults.push({
            label: instagramAccount.name,
            value: instagramAccount.id,
            category: "Instagram"
          });
          instagramAccountsInfo[instagramAccount.id] = instagramAccount;
        });
        setInstagramAccounts(instagramResults);
        setInstagramAccountsIndex(instagramAccountsInfo);
      };

      if (open) {
        const { socialMediaTypes } = profile.organization;
        const pendingSocialMediaAccounts = [];
        let personalSocialMedia = [];
        socialMediaTypes.forEach(mediaType => {
          switch (mediaType.name) {
            case "Facebook":
              if (!profile.userFacebook?.name) {
                pendingSocialMediaAccounts.push(mediaType.name);
              } else {
                const facebookAccount = {
                  label: profile.userFacebook.name,
                  value: profile.userFacebook.external_id,
                  category: "Facebook"
                };
                personalSocialMedia = [...personalSocialMedia, facebookAccount];
                fetchFacebookPages();
              }
              break;
            case "Instagram":
              if (!profile.userInstagram?.name) {
                pendingSocialMediaAccounts.push(mediaType.name);
              } else {
                const instagramAccount = {
                  label: profile.userInstagram.name,
                  value: profile.userInstagram.external_id,
                  category: "Instagram"
                };
                personalSocialMedia = [...personalSocialMedia, instagramAccount];
              }
              break;
            case "Linkedin":
              if (!profile.userLinkedin?.linkedinId) {
                pendingSocialMediaAccounts.push(mediaType.name);
              } else {
                const {
                  localizedFirstName, localizedLastName
                } = profile.userLinkedin.linkedinProfile;
                personalSocialMedia.push({
                  label: `${localizedFirstName} ${localizedLastName}`,
                  value: profile.userLinkedin.linkedinId,
                  category: "LinkedIn",
                  data: profile.userLinkedin
                });
                fetchLinkedinOrganizations();
              }
              break;
            case "TikTok":
              if (!tiktokAuthenticated(profile)) {
                pendingSocialMediaAccounts.push(mediaType.name);
              } else if (story.media.mediaType === "video") {
                const titktokAccounts = profile.userTiktoks.filter(tiktok => !!tiktok?.username)
                  .map(account => (
                    { label: account.username, value: account.username, category: "TikTok" }
                  ));
                personalSocialMedia = [...personalSocialMedia, ...titktokAccounts];
                setTiktokAccounts(titktokAccounts);
              }
              break;
            case "Twitter":
              if (!profile.userTwitter?.name) {
                pendingSocialMediaAccounts.push(mediaType.name);
              }
              break;
            default:
              break;
          }
        });
        setPersonalAccounts(personalSocialMedia);
        setPendingLinkedAccounts(pendingSocialMediaAccounts);
        setShowOverlay(pendingSocialMediaAccounts.length > 0);
      }
    }, [open, profile, story]
  );

  useEffect(
    () => {
      if (approvedContent?.message) {
        setInitialShareMessage(approvedContent.message);
      } else {
        setInitialShareMessage(story.body ? `${JSON.parse(story.body).blocks.map(block => block.text).join("\n")}` : "");
      }
    }, [approvedContent, story]
  );

  useEffect(
    () => {
      const getOrgAuthor = (category, id) => {
        switch (category) {
          case "Facebook":
            return facebookPages.find(page => page.value === id);
          case "Instagram":
            return instagramAccountsIndex[id];
          case "LinkedIn":
            return linkedinOrganizationsIndex[id];
          case "TikTok":
            return tiktokAccounts.find(account => account.value === id);
          case "Twitter":
            break;
          default:
            break;
        }
        return {};
      };

      const getPersonalAuthor = (category, id) => {
        switch (category) {
          case "Facebook":
            break;
          case "Instagram":
            break;
          case "LinkedIn":
            return profile.userLinkedin;
          case "TikTok":
            return tiktokAccounts.find(account => account.value === id);
          case "Twitter":
            break;
          default:
            break;
        }
        return {};
      };

      const getHashtags = socialMedia => {
        if (socialMedia.category === "Instagram" || socialMedia.category === "Facebook"
          || socialMedia === "Instagram" || socialMedia === "Facebook") {
          const socialMediaType = organization.organizationSocialMediaTypes.find(type => (
            type.socialMediaType === socialMedia.category || type.socialMediaType === socialMedia
          ));
          if (socialMediaType?.socialHashtags?.length > 0) {
            return `\n\n#${socialMediaType.socialHashtags[0].hashtag}`;
          }
          return `\n\n#${organization.name.toLowerCase().replaceAll(" ", "")}`;
        }
        return "";
      };

      const linkedinObject = selectedAccounts.find(selectedAccount => {
        if (typeof selectedAccount === "string") {
          return selectedAccount === "Linkedin";
        }

        return selectedAccount.category === "LinkedIn";
      });

      if (linkedinObject) {
        if (`${initialShareMessage}${getHashtags(linkedinObject)}`.length > general.LINKEDIN_CHARS_RESTRICTION) {
          setError(t("Max number of characters not allowed"));
        } else {
          setError();
        }
      } else {
        setError();
      }

      setSocialMediaSelected(selectedAccounts.map(socialMedia => ({
        category: socialMedia.category || (socialMedia === "Linkedin" ? "LinkedIn" : socialMedia),
        text: `${initialShareMessage}${getHashtags(socialMedia)}`,
        author: {
          ...socialMedia,
          ...(socialPostType === "business"
            ? getOrgAuthor(socialMedia.category, socialMedia.value)
            : getPersonalAuthor(socialMedia.category, socialMedia.value))
        }
      })));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedAccounts.length, initialShareMessage]
  );

  useEffect(
    () => {
      let options = [
        ...linkedinOrganizations, ...facebookPages, ...instagramAccounts
      ];
      if (story.media.mediaType === "video") {
        options = [...options, ...tiktokAccounts];
      }
      setCompanyAccounts(options);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [linkedinOrganizations.length, facebookPages.length,
      instagramAccounts.length, tiktokAccounts.length, story]
  );

  const handleOffsetChange = timezone => {
    setTimezoneOffset(timezone);
  };

  const handleScheduleAtChange = newScheduleAt => {
    setScheduleAt(newScheduleAt);
  };

  const handleOpen = () => {
    onOpen();
    if (onOpenChildren) onOpenChildren();
  };

  const restartValues = () => {
    setLoading(false);
    setSelectedOptions([]);
    setShowOverlay(false);
    setPendingLinkedAccounts([]);
    setSelectedAccounts([]);
    setSocialPostType("business");
    setSchedulePostType("no");
    setSocialMediaSelected([]);
    setUniqueSocialMedia([]);
  };

  const handleChangeActiveText = value => {
    if (value === "LinkedIn") {
      const linkedinObject = socialMediaSelected.find(socialMedia => socialMedia.category === "LinkedIn");
      if (linkedinObject.text.length > general.LINKEDIN_CHARS_RESTRICTION) {
        setError(t("Max number of characters not allowed"));
      } else {
        setError();
      }
    } else {
      setError();
    }
  };

  const onSocialMediaMessageChanged = (text, socialMediaToUpdate) => {
    if (!socialMediaToUpdate) {
      setInitialShareMessage(text);
    }

    if (socialMediaToUpdate === "LinkedIn" && text?.length > general.LINKEDIN_CHARS_RESTRICTION) {
      setError(t("Max number of characters not allowed"));
    } else {
      setError();
    }
    setSocialMediaSelected(socialMediaSelected.map(media => (
      media.category === socialMediaToUpdate ? { ...media, text } : media
    )));
  };

  const getBusinessAuthor = socialMedia => {
    if (socialMedia.category === "Instagram") {
      return {
        authorId: socialMedia.author.value,
        authorName: socialMedia.author.name,
        authorPhoto: socialMedia.author.profilePictureUrl,
        authorUsername: socialMedia.author.username
      };
    }

    return { authorId: socialMedia.author.value };
  };

  const getAuthor = socialMedia => {
    if (socialPostType === "business") {
      return getBusinessAuthor(socialMedia);
    }

    return { authorId: socialMedia.author.value };
  };

  const onApproveSocialClicked = async () => {
    setLoading(true);
    let state = "";
    switch (externalState) {
      case "approved":
        state = "social";
        break;
      case "landing_page":
        state = "approved_for_everything";
        break;
      default:
        state = externalState;
        break;
    }

    let shares = [];
    shares = socialMediaSelected;

    if (!isTiktokShareable) {
      shares = socialMediaSelected.filter(socialMedia => socialMedia.category !== "TikTok");
    }

    const body = {
      externalState: state,
      type: socialPostType,
      storyId: story.id,
      shares: shares.map(media => {
        if (media.category === "LinkedIn") {
          const share = {
            socialMedia: media.category.toLowerCase(),
            socialShareType: socialPostType,
            visibility: "PUBLIC",
            message: media.text,
            linkedinAuthor: media.author.value
          };
          if (socialPostType === "business") {
            share.orgName = media.author.localizedName;
            share.orgLogo = media.author.logoV2["original~"].elements[0].identifiers[0].identifier;
          }
          return share;
        }

        return {
          socialMedia: media.category.toLowerCase(),
          message: media.text,
          socialShareType: socialPostType,
          ...getAuthor(media)
        };
      }),
      selectedOptions,
      scheduledAt: scheduleAt
    };

    const { approvedForSocial } = await makePatch(`/stories/approved/${approvedContent.id}`, body);
    if (onUpdateApprovedForSocial) {
      onUpdateApprovedForSocial(approvedForSocial);
    }

    if (socialPostType === "personal" && !scheduleAt && onShareToQRLink) {
      onShareToQRLink(socialMediaSelected.filter(socialMedia => socialMedia.category === "Facebook" || socialMedia.category === "Instagram"));
    }

    handleClose();
  };

  const onHideOverlay = () => {
    setShowOverlay(false);
  };

  const goToLinkSocialAccount = () => {
    history.push("/profile/settings/social");
  };

  const onChooseSocialPostChanged = value => {
    setError();
    setSocialPostType(value);
    setSocialMediaSelected([]);
    setSelectedAccounts([]);
  };

  const onChooseSchedulePostChanged = value => {
    setSchedulePostType(value);
  };

  const onSelectSocialMedia = values => {
    setSelectedAccounts(values);
  };

  const handleClose = () => {
    restartValues();
    if (onCloseModal) onCloseModal();
    onClose();
  };

  return (
    <>
      {children(handleOpen)}
      {open && (
        <ChqModalDialog
          className="ApproveForSocial"
          onClose={handleClose}
        >
          <ModalDialog.Heading onClose={handleClose}>
            <Trans>Share This Story on Social</Trans>
          </ModalDialog.Heading>
          <ModalDialog.Body>
            <div className="col-sm-6 Input">
              <FormData
                error={error}
                initialShareMessage={initialShareMessage}
                isTiktokShareable={isTiktokShareable}
                selectedOptions={selectedOptions}
                setSelectedOptions={setSelectedOptions}
                showOverlay={showOverlay}
                socialPostType={socialPostType}
                onChooseSocialPostChanged={onChooseSocialPostChanged}
                companyAccounts={companyAccounts}
                selectedAccounts={selectedAccounts}
                onSelectSocialMedia={onSelectSocialMedia}
                socialMediaSelected={socialMediaSelected}
                uniqueSocialMedia={uniqueSocialMedia}
                onSocialMediaMessageChanged={onSocialMediaMessageChanged}
                personalAccounts={personalAccounts}
                onChooseSchedulePostChanged={onChooseSchedulePostChanged}
                schedulePostType={schedulePostType}
                profile={profile}
                story={story}
                timezoneOffset={timezoneOffset}
                scheduleAt={scheduleAt}
                handleScheduleAtChange={handleScheduleAtChange}
                handleOffsetChange={handleOffsetChange}
                onChangeActiveText={handleChangeActiveText}
              />
              <div className={classnames("Overlay", { Display: showOverlay })}>
                <p>
                  <Trans>You have not linked your</Trans>
                  {" "}
                  {pendingLinkedAccounts.map((pendinAccount, index) => (
                    <span key={pendinAccount}>
                      {pendinAccount}
                      {(pendingLinkedAccounts.length - 1) > index && (<><span>,</span>{" "}</>)}
                    </span>
                  ))}
                  {" "}
                  <Trans>account{pendingLinkedAccounts.length > 1 ? "s" : ""} to your user</Trans>
                </p>
                <p><Trans>Do you want to link {pendingLinkedAccounts.length > 1 ? "them" : "it"} now?</Trans></p>
                <div className="Buttons-Container">
                  <Button inverted onClick={onHideOverlay}><Trans>Not now</Trans></Button>
                  <Button className="Primary-Button" onClick={goToLinkSocialAccount}><Trans>Yes</Trans></Button>
                </div>
              </div>
            </div>
            <div className="col-sm-6 Preview no-scrollbar">
              <h4 className="Preview-Title"><Trans>Live Previews</Trans></h4>
              {socialMediaSelected.length === 0
                ? (
                  <EmptyPreview story={story} />
                ) : (
                  <>
                    {uniqueSocialMedia.map(media => (
                      <PreviewCard
                        key={media.category}
                        data={media}
                        category={media.category}
                        profile={profile}
                        socialPostType={socialPostType}
                        story={story}
                      />
                    ))}
                  </>
                )}
            </div>
            {loading && <Loader loading />}
          </ModalDialog.Body>
          <ModalDialog.Footer>
            {schedulePostType === "yes" && (
              <span className="scheduleat-time">
                <Icon icon="calendar-clock" />
                <Trans>Schedule for: </Trans>
                {" "}
                <Time time={scheduleAt} format="%b %e %Y, %l:%M %p" />
              </span>
            )}
            <ActionButton id="btn-save" disabled={loading || showOverlay || !!error} onClick={onApproveSocialClicked}>
              {(externalState === "social" || externalState === "approved_for_everything") ? (
                <Trans>Save and {schedulePostType === "yes" ? "Schedule" : "Share"}</Trans>
              ) : <Trans>Approve</Trans>}
            </ActionButton>
          </ModalDialog.Footer>
        </ChqModalDialog>
      )}
    </>
  );
};

export default ApproveContentModal;
